import { useState, useEffect, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import isEqual from 'lodash/isEqual';

// components
import { SelectInput } from '@interstate/components/SelectInput';
import { Alert } from '@interstate/components/Alert';
import ActionButton from '../common/ActionButton';
import { Button } from '@interstate/components/Button';
import { SlideOut } from '@interstate/components/SlideOut';
import { Grid } from '@interstate/components/Grid';
import { DatePicker } from '@interstate/components/DatePicker';
import { SaleDateField } from './SaleDateField.interstate';
import { DeliveredDateField } from './DeliveredDateField.interstate';
import { LoadingSection } from '@makemydeal/dr-shared-components';
import { dealLockHooks } from '../hooks';
import { DiscardChangesModal } from '@makemydeal/dr-shared-components';

// libraries
import type { DealLifecycle } from '@makemydeal/dr-dash-types';
import * as dealLifeCycleActionCreators from '@makemydeal/dr-dash-store/actions/dealLifeCycleActionCreators';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as dealLifecycleSelectors from '@makemydeal/dr-dash-store/selectors/dealLifecycleSelectors';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import { Typography } from '@interstate/components/Typography';
import { MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { dateUtils } from '@makemydeal/dr-common-utils';
import { isAfterDate, isBeforeDate } from '@interstate/components/Validation';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { BreakPoint } from '@makemydeal/dr-activities-common';

// hooks
import { useDispatch, useSelector } from 'react-redux';

// consts/enums
import { SALE, DELIVERED, CONTRACT_SIGNED, APPROVED, DATE_FORMAT, DealLifeCycleStatus } from '../constants';

// styles
import { DealLifeCycleFooterContainer } from './Style';
import { interstateThemeRegistry } from '@interstate/components/InterstateTheming/themes/interstateThemeRegistry';
import { InterstateThemeProvider } from '@interstate/components/InterstateThemeProvider';
import DealComments from '../dealComments/DealComments';

// utils
import { getMidDayUTCTime } from '../utils/formatUtils';

type ErrorType = {
    attempts: number;
    titleMessage: string;
    message: (() => JSX.Element) | null;
};

export type DealLifecycleModalProps = {
    show: boolean;
    dealLifecycle: DealLifecycle;
};

const initialErrorState = {
    attempts: 0,
    titleMessage: '',
    message: null
};

const oneYearAgoFromNow = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
const oneYearAgoFromNowToValidate = dateUtils.addDaysToDate(oneYearAgoFromNow, -1);

export const DealLifecycleModal = (props: DealLifecycleModalProps) => {
    const { show, dealLifecycle } = props;
    const { updateDealLifecycleStatus } = dealLifecycle;
    const { updateDealLifeCycleData, updateDealLifeCycleReady } = dealLifeCycleActionCreators;
    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();
    const [dealStatusValue, setDealStatusValue] = useState(dealLifecycle.status || DealLifeCycleStatus.IN_PROGRESS);
    const [saleFieldValue, setSaleFieldDate] = useState(dealLifecycle.soldDate || '');
    const [approvedFieldValue, setApprovedFieldDate] = useState(dealLifecycle.approvedDate || '');
    const [contractSignedFieldValue, setContractSignedFieldDate] = useState(dealLifecycle.contractSignedDate || '');
    const [deliveredFieldValue, setDeliveredFieldDate] = useState(dealLifecycle.deliveredDate || '');
    const [error, setError] = useState<ErrorType>(initialErrorState);
    const [showDiscardModal, setShowDiscardModal] = useState(false);

    const maxAllowedFutureMonthsInDateField = useSelector(configSelectors.getMaxAllowedFutureMonthsInDateField);
    const maxAllowedDate = new Date(new Date().setMonth(new Date().getMonth() + maxAllowedFutureMonthsInDateField));
    const maxAllowedDateToValidate = dateUtils.addDaysToDate(maxAllowedDate, 1);
    const isDealStatusSold = dealStatusValue === DealLifeCycleStatus.SOLD;
    const isValidDates = useMemo(() => {
        return (
            (!saleFieldValue ||
                (isBeforeDate(new Date(saleFieldValue), maxAllowedDateToValidate) &&
                    isAfterDate(new Date(saleFieldValue), oneYearAgoFromNowToValidate))) &&
            (!contractSignedFieldValue ||
                (isBeforeDate(new Date(contractSignedFieldValue), maxAllowedDateToValidate) &&
                    isAfterDate(new Date(contractSignedFieldValue), oneYearAgoFromNowToValidate))) &&
            (!deliveredFieldValue ||
                (isBeforeDate(new Date(deliveredFieldValue), maxAllowedDateToValidate) &&
                    isAfterDate(new Date(deliveredFieldValue), oneYearAgoFromNowToValidate))) &&
            (!approvedFieldValue ||
                (isBeforeDate(new Date(approvedFieldValue), maxAllowedDateToValidate) &&
                    isAfterDate(new Date(approvedFieldValue), oneYearAgoFromNowToValidate)))
        );
    }, [saleFieldValue, contractSignedFieldValue, deliveredFieldValue, maxAllowedDateToValidate, approvedFieldValue]);

    const isUpdateButtonDisabled = (isDealStatusSold && saleFieldValue === '') || !isValidDates;
    const isFieldEmpty = (fieldValue: string): boolean => {
        return fieldValue === '';
    };

    const getDateFieldDisabledState = (dateFieldValue: string | undefined, status = dealStatusValue) =>
        (status === DealLifeCycleStatus.IN_PROGRESS || status === DealLifeCycleStatus.LOST) && isFieldEmpty(dateFieldValue || '');

    const isApprovedDateDisabled = getDateFieldDisabledState(dealLifecycle.approvedDate);
    const isContractSignedDateDisabled = getDateFieldDisabledState(dealLifecycle.contractSignedDate);
    const setDate = (event: any, dateField: string) => {
        const userEnteredDate = event.target.value;
        const checkUserEnteredDate = userEnteredDate.dateValue === undefined ? '' : userEnteredDate.dateValue;
        if (dateField === SALE) {
            setSaleFieldDate(checkUserEnteredDate);
        } else if (dateField === APPROVED) {
            setApprovedFieldDate(checkUserEnteredDate);
        } else if (dateField === CONTRACT_SIGNED) {
            setContractSignedFieldDate(checkUserEnteredDate);
        } else if (dateField === DELIVERED) {
            setDeliveredFieldDate(checkUserEnteredDate);
        }
    };
    const isMvLiteEnabled = useSelector(dealerSelectors.isMvLiteEnabled);
    const showDealComments = useSelector(dealerSelectors.isDealCommentsMVEnabled) && !isMvLiteEnabled;
    const isMobile = useMediaQuery({ query: `(max-width: ${BreakPoint.PHONE})` });
    const gridSize = isMobile ? 12 : 6;
    const showDealLifeCycleLoader = useSelector(dealLifecycleSelectors.getShowDealLifeCycleModalLoader);
    const enableDealLifeCycleUpdates = true;

    /* istanbul ignore next */
    const updateDealLifeCycleDataHandler = () => {
        const {
            showDealLifeCycleModal: _showDealLifeCycleModal,
            showDealLifeCycleModalLoader: _showDealLifeCycleModalLoader,
            updateDealLifecycleStatus: _updateDealLifecycleStatus,
            hasContractSignDateUpdated: _hasContractSignDateUpdated,
            ...dealLifeCycleData
        } = dealLifecycle;
        dispatch(
            updateDealLifeCycleData(
                MANAGER_VIEW_APP_PREFIX,
                {
                    ...dealLifeCycleData,
                    status: dealStatusValue,
                    soldDate: getMidDayUTCTime(saleFieldValue, DATE_FORMAT),
                    contractSignedDate: getMidDayUTCTime(contractSignedFieldValue, DATE_FORMAT),
                    deliveredDate: getMidDayUTCTime(deliveredFieldValue, DATE_FORMAT),
                    approvedDate: getMidDayUTCTime(approvedFieldValue, DATE_FORMAT)
                },
                enableDealLifeCycleUpdates
            )
        );
        dispatch(interactionAnalyticsActionCreators.dealProgressUpdateButton());
    };

    const updateDealStatusValue = (event: any) => {
        const newStatus = event.target.value;
        if (newStatus === DealLifeCycleStatus.SOLD && !saleFieldValue) {
            setSaleFieldDate(dateUtils.formatAsMMDDYYYY(new Date()));
        } else if (newStatus === DealLifeCycleStatus.LOST || newStatus === DealLifeCycleStatus.IN_PROGRESS) {
            if (approvedFieldValue && getDateFieldDisabledState(dealLifecycle.approvedDate, newStatus)) {
                setApprovedFieldDate('');
            }
            if (contractSignedFieldValue && getDateFieldDisabledState(dealLifecycle.contractSignedDate, newStatus)) {
                setContractSignedFieldDate('');
            }
            if (deliveredFieldValue && getDateFieldDisabledState(dealLifecycle.deliveredDate, newStatus)) {
                setDeliveredFieldDate('');
            }
            if (saleFieldValue && getDateFieldDisabledState(dealLifecycle.soldDate, newStatus)) {
                setSaleFieldDate('');
            }
        }
        setDealStatusValue(event.target.value);
    };

    // Create a handler for showing the modal
    const handleShowDealLifeCycleModal = (value: boolean) => {
        dispatch(dealLifeCycleActionCreators.showDealLifeCycleModal(value));
    };

    const hasUnsavedChanges = (): boolean => {
        const currentState = {
            status: dealStatusValue,
            soldDate: saleFieldValue,
            approvedDate: approvedFieldValue,
            contractSignedDate: contractSignedFieldValue,
            deliveredDate: deliveredFieldValue
        };

        const originalState = {
            status: dealLifecycle.status || DealLifeCycleStatus.IN_PROGRESS,
            soldDate: dealLifecycle.soldDate || '',
            approvedDate: dealLifecycle.approvedDate || '',
            contractSignedDate: dealLifecycle.contractSignedDate || '',
            deliveredDate: dealLifecycle.deliveredDate || ''
        };

        return !isEqual(currentState, originalState);
    };

    const cancel = () => {
        if (hasUnsavedChanges()) {
            setShowDiscardModal(true);
        } else {
            handleShowDealLifeCycleModal(false);
        }
    };

    const handleDiscardChanges = () => {
        setShowDiscardModal(false);
        handleShowDealLifeCycleModal(false);
    };

    const handleGoBack = () => {
        setShowDiscardModal(false);
    };

    useEffect(() => {
        if (dealLifecycle.soldDate !== saleFieldValue) {
            setSaleFieldDate(dealLifecycle.soldDate || '');
        }
        if (dealLifecycle.deliveredDate !== deliveredFieldValue) {
            setDeliveredFieldDate(dealLifecycle.deliveredDate || '');
        }
        if (dealLifecycle.status !== dealStatusValue) {
            setDealStatusValue(dealLifecycle.status || DealLifeCycleStatus.IN_PROGRESS);
        }
        if (dealLifecycle.contractSignedDate !== contractSignedFieldValue) {
            setContractSignedFieldDate(dealLifecycle.contractSignedDate || '');
        }
    }, [dealLifecycle.soldDate, dealLifecycle.deliveredDate, dealLifecycle.status, dealLifecycle.contractSignedDate]);

    useEffect(() => {
        if (updateDealLifecycleStatus === 'success') {
            setTimeout(() => {
                handleShowDealLifeCycleModal(false);
            }, 500);

            setError(initialErrorState);
        }

        if (updateDealLifecycleStatus === 'failure') {
            if (error.attempts < 2) {
                setError((prev) => ({
                    attempts: prev.attempts + 1,
                    titleMessage: 'An Error Has Ocurred',
                    message: () => <p>There's a problem updating. Please try again.</p>
                }));
            } else {
                setError((prev) => ({
                    attempts: prev.attempts + 1,
                    titleMessage: 'The System Is Currently Unavailable',
                    message: () => (
                        <p data-testid="third-attempt-error-message">
                            Sorry, the system you're attempting to update is currently unavailable. Please contact Support at{' '}
                            <a href="tel:888-740-2165">888-740-2165</a>
                        </p>
                    )
                }));
            }
        }
    }, [updateDealLifecycleStatus]);

    useEffect(() => {
        if (show) {
            setDealStatusValue(dealLifecycle.status || 'InProgress');
            setSaleFieldDate(dealLifecycle.soldDate || '');
            setApprovedFieldDate(dealLifecycle.approvedDate || '');
            setContractSignedFieldDate(dealLifecycle.contractSignedDate || '');
            setDeliveredFieldDate(dealLifecycle.deliveredDate || '');
            setError(initialErrorState);
        }
    }, [show]);

    useEffect(() => {
        setTimeout(() => {
            dispatch(updateDealLifeCycleReady());
        }, 500);
    }, [error]);

    useEffect(() => {
        if (show) {
            setError(initialErrorState);
            dispatch(updateDealLifeCycleReady());
        }
    }, [show]);

    const selectInput = (
        <SelectInput
            options={[
                {
                    label: 'In Progress',
                    value: DealLifeCycleStatus.IN_PROGRESS
                },
                {
                    label: 'Lost',
                    value: DealLifeCycleStatus.LOST
                },
                {
                    label: 'Sold',
                    value: DealLifeCycleStatus.SOLD,
                    disabled: false
                }
            ]}
            onChange={updateDealStatusValue}
            name="change-deal-status"
            data-testid="change-deal-status"
            label="Change Deal Status"
            value={dealStatusValue}
            displayDeselectOption={false}
            disabled={isLocked}
        />
    );

    const saleDateFieldComponent = (
        <SaleDateField id="sale-date-picker" value={saleFieldValue} onChange={setDate} dealStatusValue={dealStatusValue} />
    );

    const approvedDateField = (
        <>
            <DatePicker
                id="approved-date-picker"
                label={APPROVED}
                dateFormat={DATE_FORMAT}
                value={approvedFieldValue ? new Date(approvedFieldValue) : null}
                onChange={(e) => {
                    setDate(e, APPROVED);
                }}
                disabled={isApprovedDateDisabled || isLocked}
                locale="en-US"
                maxDate={maxAllowedDate}
                minDate={oneYearAgoFromNow}
            />
        </>
    );

    const contractSignedDateField = (
        <>
            <DatePicker
                id="contract-signed-date-picker"
                label={CONTRACT_SIGNED}
                dateFormat={DATE_FORMAT}
                value={contractSignedFieldValue ? new Date(contractSignedFieldValue) : null}
                onChange={(e) => {
                    setDate(e, CONTRACT_SIGNED);
                }}
                disabled={isContractSignedDateDisabled || isLocked}
                required={false}
                locale="en-US"
                maxDate={maxAllowedDate}
                minDate={oneYearAgoFromNow}
                hasError={false}
            />
        </>
    );

    const deliveryDateFieldComponent = (
        <DeliveredDateField
            id="delivered-date-picker"
            value={deliveredFieldValue}
            onChange={setDate}
            dealStatusValue={dealStatusValue}
        />
    );

    const slideOutHeader = (
        <Typography sx={{ fontSize: '20px', lineHeight: '24px' }} tag="div" variant="strong-sm">
            Manage This Deal’s Progress
        </Typography>
    );

    const attempMessage = error.attempts >= 1 && (
        <div data-testid="update-dealLifecycle-error">
            <Alert role="alert" id="update-dealLifecycle-error" type="caution">
                <strong>{error.titleMessage}</strong>
                <br />
                {error.message && error.message()}
            </Alert>
        </div>
    );
    const slideOutFooter = (
        <DealLifeCycleFooterContainer gap={2}>
            <Button
                size="small"
                buttonStyle="tertiary"
                onClick={cancel}
                sx={{
                    height: '40px',
                    padding: '12px'
                }}
                data-testid="slideout-footer-cancel-button"
            >
                Close
            </Button>
            <ActionButton
                status={updateDealLifecycleStatus}
                prefix="update-btn"
                readyPrefix="ready-button"
                successAction="email-sent"
                onAction={updateDealLifeCycleDataHandler}
                actionText={'Update'}
                loadingText={'Updating...'}
                disabled={isUpdateButtonDisabled || error.attempts > 2 || isLocked}
                sx={{ height: '40px' }}
            />
        </DealLifeCycleFooterContainer>
    );

    return (
        <>
            <DiscardChangesModal
                show={showDiscardModal}
                onDiscard={handleDiscardChanges}
                onGoBack={handleGoBack}
                hasValidationErrors={isUpdateButtonDisabled}
            />
            <div data-testid="dr-dash-deal-status-modal">
                <div data-testid="deal-status-modal">
                    <InterstateThemeProvider
                        themeName="Interstate"
                        applicationName="Manager View"
                        scopeName="Manager View"
                        themeRegistries={[interstateThemeRegistry]}
                    >
                        {
                            <SlideOut
                                header={slideOutHeader}
                                panelWidth={'486px'}
                                show={show}
                                onHide={cancel}
                                position="right"
                                footer={{
                                    footerComponent: slideOutFooter,
                                    layout: 'rightAligned'
                                }}
                                sx={{
                                    '.ids-SlideOut-content': {
                                        padding: '0px 0px 48px 0px'
                                    }
                                }}
                            >
                                <LoadingSection loading={showDealLifeCycleLoader} opacity={1} />
                                <Grid
                                    data-testid="deal-status-slideout-panel-grid"
                                    sx={{ paddingLeft: '24px', paddingRight: '24px' }}
                                >
                                    {attempMessage}
                                    {selectInput}
                                </Grid>
                                <Typography
                                    sx={{
                                        margin: '24px 0 24px 0',
                                        fontSize: '18px',
                                        lineHeight: '22px',
                                        paddingLeft: '24px',
                                        paddingRight: '24px'
                                    }}
                                    tag="div"
                                    variant="strong-sm"
                                >
                                    Add Milestone Date
                                </Typography>
                                <Grid container spacing={2} sx={{ paddingLeft: '24px', paddingRight: '24px' }}>
                                    <Grid xs={gridSize}>{saleDateFieldComponent}</Grid>
                                    <Grid xs={gridSize}>{approvedDateField}</Grid>
                                    <Grid xs={gridSize}>{contractSignedDateField}</Grid>
                                    <Grid xs={gridSize}>{deliveryDateFieldComponent}</Grid>
                                </Grid>
                                {showDealComments && (
                                    <Grid
                                        container
                                        spacing={2}
                                        sx={{
                                            paddingLeft: '32px',
                                            paddingRight: '32px',
                                            paddingTop: '24px',
                                            paddingBottom: '24px'
                                        }}
                                    >
                                        <DealComments styleVariant="deal-comments-drawer" />
                                    </Grid>
                                )}
                            </SlideOut>
                        }
                    </InterstateThemeProvider>
                </div>
            </div>
        </>
    );
};
